import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Outlet, Link } from "react-router-dom";
import parse from "html-react-parser";
import { CoIBooks, PPBooks, ContemporaryRomance, KotRBooks, KotRSeries } from "../Components/Books";
import "../CSS_stuff/format_fiction.css";
import KotRSeriesBanner from "../assets/KotRMAin-4-WoS-1-squiggle.jpg";
import CoISeries from "../assets/2025-CoI-book-banner.jpg";
import PPseries from "../assets/2025-PP-book-banner.jpg";
import CRseries from "../assets/2025-Contemp-Rom-book-banner.jpg";

// Series metadata: add long HTML description for Knightess (you can edit it)
const seriesMeta = {
  "knightess-of-the-realm": {
    label: "Knightess of the Realm",
    img: KotRSeriesBanner,
    bannerBook: "KotR5-SDM1",
    tagline: `Ten thousand years… and it all comes down to one young woman.
Family first. Always.
`,
    // Put the long text here. HTML is allowed (e.g. <strong>, <em>, <p>, <ul>, <br/>).
    // Edit this string to change the displayed content.
    kickstartertext: ` <strong><em>An All-Too-Easy-Answer (Book 1 of the Wedding of the Season),</em></strong> the start of a fresh new mini-series in the continuing tale of Karana’s adventures.
      <p>
        <strong>FIRST TIME RELEASE for this KICKSTARTER PROJECT!</strong><br>
        Be the first to find out how everyone’s favorite knightess, dragonkeeper, and Head of mercantile empire can end up in a mess while she’s simply trying to get
        married!
        Will she rescue herself (again) – or do Kefen, Ivan, and the dragonlings have a shot at helping her?
      </p>,`,
    description: `
      <p>
        <strong><u>The KNIGHTESS of the REALM Series</u></strong>
      </p>

      <p>
        <em>Ten thousand years of planning, and it all comes down to one young woman…</em>
      </p>

      <p>
            Karana never planned to become the first knightess in a century. Or to find her fated mates. She hadn’t even <em>particularly</em> wanted to follow her papa as
            Head of House Metreedi and keep up The Family’s Goddess-given Onus to protect the peace and prosperity of the known world.
      </p>

      <p>
            And becoming one of the Realm’s first dragonkeepers in a thousand years – well, she’d sort of fantasized about that, because what little girl didn’t?
            Handsome princes and dragons – each as out of reach as the other.
      </p>

      <p>
            She wanted to captain her own ship and explore the far reaches of the known world. To discover places even the Metreedi Family had never touched –
            for, ahem, the purposes of starting trade with them of course. The <em>responsible</em> reason to do things.
      </p>

      <p>
            The Family calls her ‘Hurricane Karana’ – but this ‘Hurricane’ has some serious work ahead to make things work out according to <em>her</em> plan instead of
            the one the <em>Goddesses</em> have laid out for her!
      </p>

      <p>
            Come along with Karana to explore this richly detailed world and discover the exciting and mysterious hidden spaces where the real Powers That Be
            follow the dictates of the Great Goddess of All
      </p>
    `,
    kickstarterUrl: "https://www.kickstarter.com/projects/mangala-knightess/knightess-of-the-realm-spicy-epic-dragon-romantasy-series-0?ref=5n3dxl"
  },
  "prankster-prince": {
    label: "Prankster Prince",
    img: PPseries
  },
  "chronicles-of-ilseador": {
    label: "Chronicles of Ilseador",
    img: CoISeries
  },
  "ContemporaryRomance": {
    label: "Contemporary Romance",
    img: CRseries
  }
};

const Fiction = () => {
  const [selectedSeries, setSelectedSeries] = useState("knightess-of-the-realm");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (["knightess-of-the-realm","prankster-prince","chronicles-of-ilseador","ContemporaryRomance"].includes(hash)) {
      setSelectedSeries(hash);
    }
  }, [location]);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, 0);
  }, [selectedSeries]);

  // Map series keys to book arrays (Knightess uses KotRBooks + KotRSeries for mini-series)
  const bookLists = {
    "ContemporaryRomance": ContemporaryRomance,
    "knightess-of-the-realm": KotRBooks,
    "prankster-prince": PPBooks,
    "chronicles-of-ilseador": CoIBooks
  };
  const books = bookLists[selectedSeries] || [];

  function handleBookClick(book) {
    navigate(`/books/${book.link}`, { state: { book } });
  }

  const meta = seriesMeta[selectedSeries] || {};

  // <-- ADD: resolve the book object referenced by meta.bannerBook so we can pass it in Link state
  const bannerBookObj = useMemo(() => {
    if (!meta || !meta.bannerBook) return null;
    return KotRBooks.find((b) => b.link === meta.bannerBook) || null;
  }, [meta]);

  return (
    <div className="fiction-container">

      {/* OPTIONAL SHORT TAGLINE (renders above the banner image if present) */}
      {meta.tagline && (
        <div className="series-tagline-short" aria-hidden="false">
          {/* reuse the existing white-card classes so the tagline appears in the same white box */}
          <div className="series-highlight tagline-highlight">
            <div className="series-description tagline-description">
              {parse(meta.tagline)}
            </div>
          </div>
        </div>
      )}

      {/* SERIES BANNER (with KotR "Jump to Books" button above the banner) */}
      {selectedSeries === "knightess-of-the-realm" && meta.img && (
        <div style={{ textAlign: "center", margin: "0.75rem 0" }}>
          <button
            type="button"
            className="jump-to-books"
            onClick={() => {
              const el = document.getElementById("books-heading");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.focus && el.focus();
              }
            }}
            aria-label="Jump to books"
          >
            Jump to Books
          </button>
        </div>
      )}

      {/* SERIES BANNER: if meta.bannerBook is set, make the banner a link to that book (passes book in state) */}
      {meta.img && meta.bannerBook ? (
        <div className="series-banner">
          <Link to={`/books/${meta.bannerBook}`} state={{ book: bannerBookObj }}>
            <img src={meta.img} alt={meta.label || ""} loading="lazy" />
          </Link>
        </div>
      ) : (
        <div className="series-banner">
          <img src={meta.img} alt={meta.label || ""} loading="lazy" />
        </div>
      )}

      {/* KICKSTARTER (stacked above) + long description (stacked below) */}
      {(meta.kickstartertext || meta.description) && (
        <div className="series-top-stack" aria-live="polite">
          {/* KickStarter text box (if present) */}
          {meta.kickstartertext && (
            <div className="series-highlight kickstarter-stack">
              <div className="series-description centered">
                {parse(meta.kickstartertext)}
                {meta.kickstarterUrl && (
                  <p className="kickstarter-link" style={{ marginTop: "0.75rem" }}>
                    <a
                      href={meta.kickstarterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="kickstarter-button"
                      aria-label="Open Kickstarter (opens in a new tab)"
                    >
                      Visit the Kickstarter
                    </a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Main description box (if present) */}
          {meta.description && (
            <div className="series-highlight description-stack" style={{ marginTop: 12 }}>
              <div className="series-description centered">
                {parse(meta.description)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MINI-SERIES IMAGES (clickable) - only shown when this series has mini-series metadata (KotR) */}
      {selectedSeries === "knightess-of-the-realm" && Array.isArray(KotRSeries) && KotRSeries.length > 0 && (
        <section className="mini-series-overview" aria-labelledby="ms-overview-heading">
          <h2 id="ms-overview-heading">Mini-series</h2>
          <div className="mini-series-grid" style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {KotRSeries.map((ms, i) => (
              <div key={ms.title || i} className="mini-series-tile" style={{ textAlign: "center" }}>
                <Link
                  to={`/mini-series/${encodeURIComponent(ms.title)}`}
                  state={{ series: ms }}                    // <-- ADDED: pass full mini-series object in Link state
                  aria-label={`Open mini-series ${ms.title}`}
                >
                  {ms.image ? (
                    <img src={ms.image} alt={ms.title} style={{ width: "100%", height: "auto", borderRadius: 6 }} loading="lazy" />
                  ) : (
                    <div style={{ padding: "1rem", border: "1px solid #ccc", borderRadius: 6 }}>{ms.title}</div>
                  )}
                </Link>
                <div style={{ marginTop: 8 }}>{ms.title}</div>

                {/* render mini-series description inside the white card so CSS applies */}
                {ms.description && (
                  <div className="series-highlight" style={{ marginTop: 12 }}>
                    <div className="series-description">
                      {parse(ms.description)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* BOOK GRID: all books in the selected series */}
      {books.length > 0 && (
        <section className="book-grid-section" aria-labelledby="books-heading">
          <h2 id="books-heading">Books</h2>
          <div className="book-grid">
            {books.map(b => (
              <div
                key={b.link}
                className="book-item"
                onClick={() => handleBookClick(b)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleBookClick(b); }}
                style={{ cursor: "pointer" }}
              >
                {b.image ? <img src={b.image} alt={b.title} loading="lazy" /> : <div className="no-cover">{b.title}</div>}
                <p dangerouslySetInnerHTML={{ __html: b.title }} />
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="series-content preface">
        <p>Check out ALL of Mangala's series!</p>
      </div>

      {/* series selector */}
      <div className="series-nav">
        <button onClick={() => setSelectedSeries("knightess-of-the-realm")}>
          Knightess of the Realm
        </button>
        <button onClick={() => setSelectedSeries("prankster-prince")}>
          Prankster Prince
        </button>
        <button onClick={() => setSelectedSeries("chronicles-of-ilseador")}>
          Chronicles of Ilseador
        </button>
        <button onClick={() => setSelectedSeries("ContemporaryRomance")}>
          Contemporary Romance
        </button>
      </div>

      <Outlet />
    </div>
  );
};

export default Fiction;